import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from './msalConfig';
import HomesSection from './homes/HomesSection';
import CollapsibleSection, { openSection } from './common/CollapsibleSection';

const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const LIST_NAME = 'GroceryList';
const GRAPH = 'https://graph.microsoft.com/v1.0';

const STORES = ['Costco', 'Kroger', 'Restaurant Depot', 'Amazon', 'Custom'];

// Unchecked items, newest added first: by created time, then the higher id
// (SharePoint ids only grow). An item with no created time goes after those
// with one, by id.
export function newestFirst(items) {
  const t = (i) => { const n = Date.parse(i.created || ''); return Number.isFinite(n) ? n : -Infinity; };
  return [...items].sort((a, b) => {
    const ta = t(a); const tb = t(b);
    if (ta !== tb) return ta < tb ? 1 : -1;
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

function GroceryList() {
  const { instance, accounts } = useMsal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [store, setStore] = useState('');
  const [customStore, setCustomStore] = useState('');
  const [photo, setPhoto] = useState(null);
  const [adding, setAdding] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  // Homes (N) jump link: HomesSection reports how many cards it renders (0 = it renders nothing).
  const [homesCount, setHomesCount] = useState(0);
  const listBaseRef = useRef(null);
  const fieldNamesRef = useRef(null);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({
      ...graphRequest,
      account,
    });
    return response.accessToken;
  }, [instance, accounts]);

  const getListBase = useCallback(async (token) => {
    if (listBaseRef.current) return listBaseRef.current;

    // Step 1: resolve site ID
    const siteRes = await fetch(
      `${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!siteRes.ok) {
      const body = await siteRes.text();
      throw new Error(`Failed to resolve SharePoint site (${siteRes.status}): ${body}`);
    }
    const site = await siteRes.json();
    const siteId = site.id;

    // Step 2: fetch all lists and match by display name
    const listsRes = await fetch(
      `${GRAPH}/sites/${siteId}/lists?$select=displayName,id&$top=100`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!listsRes.ok) {
      const body = await listsRes.text();
      throw new Error(`Failed to query lists (${listsRes.status}): ${body}`);
    }
    const listsData = await listsRes.json();
    const allLists = listsData.value || [];
    const target = LIST_NAME.toLowerCase();
    const list = allLists.find(l => l.displayName.toLowerCase() === target);
    if (!list) {
      const listNames = allLists.map(l => `"${l.displayName}"`).join(', ');
      throw new Error(
        `List "${LIST_NAME}" not found. Available lists: ${listNames}`
      );
    }

    const listBase = `${GRAPH}/sites/${siteId}/lists/${list.id}`;
    listBaseRef.current = listBase;

    // Step 3: resolve internal column names
    const colsRes = await fetch(
      `${listBase}/columns?$select=displayName,name&$top=100`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (colsRes.ok) {
      const colsData = await colsRes.json();
      const cols = colsData.value || [];
      const find = (display) => {
        const col = cols.find(c => c.displayName.toLowerCase() === display.toLowerCase());
        return col?.name || display;
      };
      fieldNamesRef.current = {
        Store: find('Store'),
        Checked: find('Checked'),
        Priority: find('Priority'),
        PhotoUrl: find('PhotoUrl'),
      };
      console.log('[GroceryList] Resolved field names:', JSON.stringify(fieldNamesRef.current));
      // Ensure PhotoUrl column exists
      const hasPhotoUrl = cols.some(c => c.displayName.toLowerCase() === 'photourl');
      if (!hasPhotoUrl) {
        try {
          const colRes2 = await fetch(`${listBase}/columns`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: 'PhotoUrl', displayName: 'PhotoUrl', text: {} }),
          });
          if (colRes2.ok) {
            const colData2 = await colRes2.json();
            const internalName = colData2.name || 'PhotoUrl';
            console.log('[GroceryList] PhotoUrl column created, internal name:', internalName);
            fieldNamesRef.current.PhotoUrl = internalName;
          }
        } catch { /* column creation not critical */ }
      }
    } else {
      fieldNamesRef.current = { Store: 'Store', Checked: 'Checked', Priority: 'Priority', PhotoUrl: 'PhotoUrl' };
    }

    return listBase;
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const base = await getListBase(token);

      const res = await fetch(
        `${base}/items?$expand=fields&$top=500`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to load grocery list (${res.status}): ${body}`);
      }
      const data = await res.json();

      const fn = fieldNamesRef.current || {};
      const itemsWithAttachments = (data.value || []).map(item => ({
        id: item.id,
        created: item.createdDateTime || item.fields?.Created || null,
        title: item.fields?.Title || '',
        store: item.fields?.[fn.Store || 'Store'] || '',
        checked: item.fields?.[fn.Checked || 'Checked'] || false,
        priority: item.fields?.[fn.Priority || 'Priority'] || 'green',
        thumbnailUrl: item.fields?.[fn.PhotoUrl || 'PhotoUrl'] || null,
      }));

      setItems(itemsWithAttachments);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, getListBase]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const token = await getToken();
      const base = await getListBase(token);

      const fn = fieldNamesRef.current || {};
      const fields = { Title: name.trim(), [fn.Checked || 'Checked']: false, [fn.Priority || 'Priority']: 'green' };
      const storeValue = store === 'Custom' ? customStore.trim() : store;
      if (storeValue) fields[fn.Store || 'Store'] = storeValue;

      const res = await fetch(`${base}/items`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fields }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Failed to add item (${res.status}): ${body}`);
      }
      const created = await res.json();

      if (photo) {
        const fileName = `grocery-${created.id}-${photo.name || 'photo.jpg'}`;
        // Upload photo to site's default drive under GroceryPhotos folder
        const siteRes = await fetch(
          `${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const site = await siteRes.json();
        const driveRes = await fetch(
          `${GRAPH}/sites/${site.id}/drive/root:/GroceryPhotos/${fileName}:/content`,
          {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': photo.type || 'application/octet-stream',
            },
            body: photo,
          }
        );
        if (driveRes.ok) {
          const driveItem = await driveRes.json();
          const photoUrl = driveItem.webUrl || '';
          // Save URL to the list item
          if (photoUrl) {
            const fn = fieldNamesRef.current || {};
            const patchUrl = `${base}/items/${created.id}/fields`;
            const patchBody = { [fn.PhotoUrl || 'PhotoUrl']: photoUrl };
            console.log('[Photo] PATCH URL:', patchUrl);
            console.log('[Photo] PATCH body:', JSON.stringify(patchBody));
            console.log('[Photo] PhotoUrl field name resolved to:', fn.PhotoUrl);
            const patchRes = await fetch(patchUrl, {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify(patchBody),
            });
            console.log('[Photo] PATCH response:', patchRes.status, patchRes.ok ? 'OK' : await patchRes.text().catch(() => ''));
          }
        }
      }

      setName('');
      setStore('');
      setCustomStore('');
      setPhoto(null);
      const fileInput = document.getElementById('grocery-photo-input');
      if (fileInput) fileInput.value = '';
      await fetchItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  async function toggleChecked(item) {
    try {
      const token = await getToken();
      const base = await getListBase(token);
      const res = await fetch(
        `${base}/items/${item.id}/fields`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ [fieldNamesRef.current?.Checked || 'Checked']: !item.checked }),
        }
      );
      if (!res.ok) throw new Error(`Failed to update item (${res.status})`);
      setItems(prev =>
        prev.map(i => (i.id === item.id ? { ...i, checked: !i.checked } : i))
      );
    } catch (err) {
      setError(err.message);
    }
  }

  async function deletePhoto(token, photoUrl) {
    if (!photoUrl) return;
    try {
      // Extract path after /sites/DefineHolyFamily/ from the webUrl
      const match = photoUrl.match(/\/sites\/DefineHolyFamily\/(.+)$/i);
      if (!match) return;
      const filePath = decodeURIComponent(match[1]);
      const siteRes = await fetch(
        `${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!siteRes.ok) return;
      const site = await siteRes.json();
      await fetch(
        `${GRAPH}/sites/${site.id}/drive/root:/${filePath}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
    } catch { /* photo cleanup is best-effort */ }
  }

  async function deleteItem(item) {
    try {
      const token = await getToken();
      const base = await getListBase(token);
      const res = await fetch(
        `${base}/items/${item.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error(`Failed to delete item (${res.status})`);
      await deletePhoto(token, item.thumbnailUrl);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteAll() {
    setShowConfirmDelete(false);
    setError(null);
    try {
      const token = await getToken();
      const base = await getListBase(token);
      await Promise.all(
        items.map(async (item) => {
          await fetch(`${base}/items/${item.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          await deletePhoto(token, item.thumbnailUrl);
        })
      );
      setItems([]);
    } catch (err) {
      setError(err.message);
      await fetchItems();
    }
  }

  if (loading) {
    return <div className="Grocery"><p className="Grocery-loading">Loading grocery list...</p></div>;
  }

  const unchecked = newestFirst(items.filter(i => !i.checked));
  const checked = items.filter(i => i.checked);
  const sortedItems = [...unchecked, ...checked];

  return (
    <div className="Grocery">
      <h1 className="Grocery-title">Shopping</h1>
      {homesCount > 0 && (
        <a className="Grocery-homes-jump" href="#homes"
          onClick={(e) => { e.preventDefault(); openSection('homes'); }}>
          Homes ({homesCount}) ↓
        </a>
      )}

      {error && <p className="Grocery-error">{error}</p>}

      <CollapsibleSection id="shopping-list" title="Shopping list"
        summary={`${items.length} item${items.length === 1 ? '' : 's'} · ${checked.length} checked`}>
      <form className="Grocery-form" onSubmit={handleAdd}>
        <input
          className="Grocery-input"
          type="text"
          placeholder="Item name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />
        <select
          className="Grocery-select"
          value={store}
          onChange={e => { setStore(e.target.value); if (e.target.value !== 'Custom') setCustomStore(''); }}
        >
          <option value="">Store (optional)</option>
          {STORES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {store === 'Custom' && (
          <input
            className="Grocery-input Grocery-custom-store"
            type="text"
            placeholder="Store name"
            value={customStore}
            onChange={e => setCustomStore(e.target.value)}
          />
        )}
        <label className="Grocery-photo-label" htmlFor="grocery-photo-input">
          {photo ? photo.name : 'Add Photo'}
        </label>
        <input
          id="grocery-photo-input"
          className="Grocery-photo-input"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={e => setPhoto(e.target.files[0] || null)}
        />
        <button className="Grocery-add-btn" type="submit" disabled={adding}>
          {adding ? 'Adding...' : 'Add'}
        </button>
      </form>

      {items.length > 0 && (
        <div className="Grocery-actions">
          <span className="Grocery-count">
            {unchecked.length} remaining &middot; {checked.length} done
          </span>
          <button
            className="Grocery-delete-all-btn"
            onClick={() => setShowConfirmDelete(true)}
          >
            Delete All
          </button>
        </div>
      )}

      {showConfirmDelete && (
        <div className="Grocery-confirm">
          <p>Delete all {items.length} items? This cannot be undone.</p>
          <div className="Grocery-confirm-actions">
            <button className="Grocery-confirm-yes" onClick={deleteAll}>
              Yes, Delete All
            </button>
            <button
              className="Grocery-confirm-no"
              onClick={() => setShowConfirmDelete(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <ul className="Grocery-list">
        {sortedItems.map(item => (
          <li
            key={item.id}
            className={`Grocery-item${item.checked ? ' Grocery-item--done' : ''}${item.priority === 'red' ? ' Grocery-item--red' : item.priority === 'yellow' ? ' Grocery-item--yellow' : ''}`}
          >
            <button
              className="Grocery-check-btn"
              onClick={() => toggleChecked(item)}
              aria-label={item.checked ? 'Uncheck' : 'Check'}
            >
              <span className={`Grocery-checkbox${item.checked ? ' Grocery-checkbox--checked' : ''}`} />
            </button>
            <div className="Grocery-item-info">
              <span className="Grocery-item-name">{item.title}</span>
              <span className="Grocery-item-store">{item.store}</span>
            </div>
            {item.thumbnailUrl && (
              <img
                className="Grocery-item-thumb"
                src={item.thumbnailUrl}
                alt={item.title}
              />
            )}
            <button
              className="Grocery-delete-btn"
              onClick={() => deleteItem(item)}
              aria-label="Delete"
            >
              &times;
            </button>
          </li>
        ))}
      </ul>

      {items.length === 0 && (
        <p className="Grocery-empty">No items yet. Add something above!</p>
      )}
      </CollapsibleSection>

      {/* Homes watchlist: self-contained, renders nothing without Watch rows. */}
      <HomesSection onCount={setHomesCount} />
    </div>
  );
}

export default GroceryList;
